import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Platform } from '@angular/cdk/platform';
import { SwPush, SwUpdate } from '@angular/service-worker';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { SocketService } from 'app/main/apps/chat/socket.service';
import { LocalStorageService } from 'app/services/local-storage.service';
import { CustomerAccount } from 'app/main/pages/authentication/shared/model/user.model';
import { AuthRoles } from 'app/main/pages/authentication/shared/model/enum/auth-roles.enum';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class BrowserNotificationService {
  readonly browserNotificationsEnabledKey = 'browserNotificationsEnabled';
  readonly VAPID_PUBLIC_KEY = environment.VAPID_PUBLIC_KEY;
  private readonly mobileSubscriptionKey = 'subcription';
  private readonly roomTypes = [
    { name: 'whatsapp', value: 1 },
    { name: 'web', value: 3 },
    { name: 'messenger', value: 2 },
    { name: 'email', value: 5 },
    { name: 'instagram', value: 6 },
    { name: 'whatsapp_lite', value: 7 },
  ];

  private titleInterval: any;
  private titleTimeout: any;
  private notificationCount = 0;
  private originalDocumentTitle = document.title;
  private areRoomNotificationsSubscribed = false;
  private unsubscribeRoomNotifications = new Subject<void>();

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private _platform: Platform,
    private swPush: SwPush,
    private swUpdate: SwUpdate,
    private _fuseNavigationService: FuseNavigationService,
    private socketService: SocketService,
    private _localStorageService: LocalStorageService
  ) {
    document.addEventListener('visibilitychange', this.clearFeedback);
  }

  isSupported(): boolean {
    return 'Notification' in window;
  }

  shouldHandleNotifications(): boolean {
    const menu = document.querySelector<HTMLElement>(
      '#doppler-menu-mfe .header-main'
    );
    if (!menu || menu.getClientRects().length === 0) {
      return true;
    }

    const visibility = window.getComputedStyle(menu).visibility;
    return visibility === 'hidden' || visibility === 'collapse';
  }

  isEnabled(): boolean {
    return (
      this.isSupported() &&
      localStorage.getItem(this.browserNotificationsEnabledKey) !== 'false' &&
      (Notification as any).permission === 'granted'
    );
  }

  syncPreference(): boolean {
    const enabled = this.isEnabled();
    if (enabled) {
      localStorage.setItem(this.browserNotificationsEnabledKey, 'true');
    }
    return enabled;
  }

  enable(): Promise<boolean> {
    if (!this.shouldHandleNotifications()) {
      return Promise.resolve(false);
    }

    if (!this.isSupported()) {
      this.disable();
      return Promise.resolve(false);
    }

    if ((Notification as any).permission === 'granted') {
      localStorage.setItem(this.browserNotificationsEnabledKey, 'true');
      return Promise.resolve(true);
    }

    return (Notification as any).requestPermission().then(permission => {
      const granted = permission === 'granted';
      localStorage.setItem(
        this.browserNotificationsEnabledKey,
        granted ? 'true' : 'false'
      );
      return granted;
    });
  }

  disable(): void {
    localStorage.setItem(this.browserNotificationsEnabledKey, 'false');
    this.clearFeedback();
  }

  hasMobileSubscription(): boolean {
    return !!localStorage.getItem(this.mobileSubscriptionKey);
  }

  getMobileSubscription(): any {
    try {
      return JSON.parse(localStorage.getItem(this.mobileSubscriptionKey));
    } catch (error) {
      console.error('Error parsing push notification subscription', error);
      return null;
    }
  }

  saveMobileSubscription(subscription: any): void {
    localStorage.setItem(this.mobileSubscriptionKey, JSON.stringify(subscription));
  }

  removeMobileSubscription(): void {
    localStorage.removeItem(this.mobileSubscriptionKey);
  }

  setOriginalDocumentTitle(title: string): void {
    this.originalDocumentTitle = title || document.title;
  }

  getToggleValue(): boolean {
    return this.isMobile() ? this.hasMobileSubscription() : this.syncPreference();
  }

  syncToggleControl(control: FormControl): void {
    control.setValue(this.getToggleValue(), { emitEvent: false });
  }

  toggleNotificationControl(
    control: FormControl,
    enabled: boolean,
    onToggle?: (value: boolean) => void
  ): void {
    this.toggleNotifications(enabled).then(value => {
      if (onToggle) {
        onToggle(value);
      }
      control.setValue(value, { emitEvent: false });
    });
  }

  toggleNotifications(enabled: boolean): Promise<boolean> {
    if (!this.shouldHandleNotifications()) {
      return Promise.resolve(false);
    }

    return enabled
      ? this.subscribeToNotifications()
      : this.unsubscribeNotifications();
  }

  private subscribeToNotifications(): Promise<boolean> {
    if (this.isMobile()) {
      if (this.swUpdate.isEnabled) {
        this.swUpdate.available.subscribe(() => {
          if (confirm('New version available. Load New Version?')) {
            window.location.reload();
          }
        });
      }

      return this.swPush
        .requestSubscription({
          serverPublicKey: this.VAPID_PUBLIC_KEY,
        })
        .then(sub => {
          this.saveMobileSubscription(sub);
          return true;
        })
        .catch(err => {
          console.error(err);
          return false;
        });
    }

    return this.enable();
  }

  private unsubscribeNotifications(): Promise<boolean> {
    if (this.isMobile()) {
      return this.swPush
        .unsubscribe()
        .catch(err => console.error(err))
        .then(() => {
          this.removeMobileSubscription();
          return false;
        });
    }

    this.disable();
    return Promise.resolve(false);
  }

  subscribeToIncomingChatMessages(): void {
    if (this.areRoomNotificationsSubscribed) {
      return;
    }

    this.roomTypes.forEach(element => {
      this.socketService[`${element.name}` + 'MethodCallSource']
        .pipe(takeUntil(this.unsubscribeRoomNotifications))
        .subscribe(msj => this.notifyChatMessage(msj));
    });
    this.areRoomNotificationsSubscribed = true;
  }

  private notifyChatMessage(msj: any): void {
    const currentUser = this._localStorageService.getCurrentUser();
    if (!this.shouldNotifyUser(currentUser, msj)) {
      return;
    }

    this.notifyMessage(msj);
  }

  private shouldNotifyUser(currentUser: CustomerAccount, msj: any): boolean {
    if (!currentUser || !msj || !msj.chatMessage) {
      return false;
    }

    const chatMessage = msj.chatMessage;
    if (`${chatMessage.customerId}` !== `${currentUser.customerId}`) {
      return false;
    }

    if (this.isMessageFromActiveFocusedChat(msj)) {
      return false;
    }

    const messageType = `${chatMessage.messageType}`.trim();
    if (messageType === 'IN') {
      return this.shouldNotifyIncomingMessage(currentUser, msj);
    }

    return (
      messageType === 'OUT' &&
      chatMessage.chat &&
      chatMessage.chat.state === 'NOTVIEW'
    );
  }

  private shouldNotifyIncomingMessage(
    currentUser: CustomerAccount,
    msj: any
  ): boolean {
    const authorities = currentUser.authorities || [];
    if (authorities.includes(AuthRoles.ROLE_ADMIN)) {
      return true;
    }

    if (!authorities.includes(AuthRoles.ROLE_ATC)) {
      return false;
    }

    const rooms = this.getCurrentUserRooms();
    const found = rooms.find(
      room => `${room.room.id}` === `${msj.chatMessage.chat.roomId}`
    );
    if (!found) {
      return false;
    }

    const { customerUserId, customeruserId } = msj.chatMessage.chat;
    const assignedCustomerUserId = customerUserId || customeruserId;
    const modality =
      found.room && found.room.modality
        ? `${found.room.modality}`.toUpperCase()
        : null;

    return (
      !assignedCustomerUserId ||
      modality === 'OPEN' ||
      `${assignedCustomerUserId}` === `${currentUser.id}`
    );
  }

  private isMessageFromActiveFocusedChat(msj: any): boolean {
    if (
      document.hidden ||
      (typeof document.hasFocus === 'function' && !document.hasFocus()) ||
      !this.router.url.startsWith('/apps/chat')
    ) {
      return false;
    }

    const currentChat = this.getCurrentChat();
    const chatMessage = msj && msj.chatMessage ? msj.chatMessage : null;
    const incomingChatId =
      chatMessage && chatMessage.chat
        ? chatMessage.chat.id
        : chatMessage
          ? chatMessage.chatId
          : null;
    const currentChatId = currentChat ? currentChat.chatId || currentChat.id : null;

    return (
      incomingChatId !== null &&
      currentChatId !== null &&
      `${incomingChatId}` === `${currentChatId}`
    );
  }

  private getCurrentChat(): any {
    const currentChatJson = sessionStorage.getItem('currentChat');
    if (!currentChatJson) {
      return null;
    }

    try {
      return JSON.parse(currentChatJson);
    } catch (error) {
      console.error('Error parsing current chat from sessionStorage', error);
      return null;
    }
  }

  private getCurrentUserRooms(): any[] {
    const roomsJson = sessionStorage.getItem('myRooms');
    if (!roomsJson) {
      return [];
    }

    try {
      return JSON.parse(roomsJson);
    } catch (error) {
      console.error('Error parsing rooms from sessionStorage', error);
      return [];
    }
  }

  private notifyMessage(msj?: any): void {
    if (!this.shouldHandleNotifications()) {
      return;
    }

    if (this.isMobile()) {
      const sub = this.getMobileSubscription();
      if (sub) {
        this._fuseNavigationService.sendNotification(sub).subscribe(() => {});
      }
      return;
    }

    this.notifyNewMessage(msj);
  }

  notifyNewMessage(msj?: any): void {
    if (!this.shouldHandleNotifications() || !this.isEnabled()) {
      return;
    }

    localStorage.setItem(this.browserNotificationsEnabledKey, 'true');
    this.showWindowNotification(
      {
        icon: 'assets/doppler/favicon.ico',
        body: this.getMessagePreview(msj),
        requireInteraction: true,
        renotify: true,
        silent: false,
        tag: 'doppler-conversations-new-message',
      },
      msj
    );
    this.showFeedback();
  }

  clearFeedback = (): void => {
    if (document.hidden) {
      return;
    }

    this.resetFeedback();
  };

  private resetFeedback(restoreTitle = true): void {
    if (this.titleInterval) {
      clearInterval(this.titleInterval);
      this.titleInterval = null;
    }
    clearTimeout(this.titleTimeout);
    this.titleTimeout = null;
    this.notificationCount = 0;
    if (restoreTitle && this.shouldHandleNotifications()) {
      document.title = this.originalDocumentTitle;
    }
  }

  private showWindowNotification(options: NotificationOptions, msj?: any): void {
    try {
      const notification = new (Notification as any)(
        this.translateService.instant('BROWSER_NOTIFICATION.TITLE'),
        options
      );
      notification.onclick = () => {
        this.openChat(msj);
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('Error showing browser notification', error);
    }
  }

  private openChat(msj?: any): void {
    const chat = msj && msj.chatMessage ? msj.chatMessage.chat : null;
    if (!chat || !chat.roomId || !chat.id) {
      return;
    }

    this.router.navigate(['/apps/chat/all', chat.roomId], {
      queryParams: { chatId: chat.id },
    });
  }

  private getMessagePreview(msj?: any): string {
    const chatMessage = msj && msj.chatMessage ? msj.chatMessage : null;
    const chat = chatMessage && chatMessage.chat ? chatMessage.chat : null;
    const contactName =
      chat && chat.contactName
        ? chat.contactName
        : this.translateService.instant('BROWSER_NOTIFICATION.NEW_MESSAGE');
    const messageText = this.getMessageText(chatMessage);

    return messageText
      ? `${contactName}: ${messageText}`
      : `${contactName}: ${this.translateService.instant(
          'BROWSER_NOTIFICATION.DEFAULT_BODY'
        )}`;
  }

  private getMessageText(chatMessage: any): string {
    if (!chatMessage) {
      return null;
    }

    const text =
      chatMessage.text ||
      chatMessage.messageText ||
      chatMessage.mediaCaption ||
      chatMessage.subject;

    if (!text || this.isTechnicalMessage(text)) {
      return null;
    }

    const normalizedText = `${text}`.replace(/\s+/g, ' ').trim();
    return normalizedText.length > 120
      ? `${normalizedText.substring(0, 117)}...`
      : normalizedText;
  }

  private isTechnicalMessage(text: string): boolean {
    return [
      'change_rooms_assigned',
      'change_modality',
      'chat_update',
      'on_label_assigned',
      'on_followed_message',
      'on_read_mail',
      'not_null',
      'null',
    ].includes(`${text}`);
  }

  private showFeedback(): void {
    this.notificationCount += 1;
    this.playSound();

    if (!this.titleInterval) {
      this.originalDocumentTitle = document.title || this.originalDocumentTitle;
      this.titleInterval = setInterval(() => {
        if (!this.shouldHandleNotifications()) {
          this.resetFeedback(false);
          return;
        }

        document.title =
          document.title === this.originalDocumentTitle
            ? `(${this.notificationCount}) ${this.translateService.instant(
                'BROWSER_NOTIFICATION.TAB_TITLE'
              )}`
            : this.originalDocumentTitle;
      }, 1000);
    }

    if (!document.hidden) {
      clearTimeout(this.titleTimeout);
      this.titleTimeout = setTimeout(() => {
        this.clearFeedback();
      }, 10000);
    }
  }

  private playSound(): void {
    try {
      const AudioContext =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioContext) {
        return;
      }

      const audioContext = new AudioContext();
      const notes = [
        { frequency: 659.25, start: 0, duration: 0.12 },
        { frequency: 783.99, start: 0.1, duration: 0.13 },
        { frequency: 987.77, start: 0.21, duration: 0.18 },
      ];

      notes.forEach(note => {
        const oscillator = audioContext.createOscillator();
        const gain = audioContext.createGain();
        const startsAt = audioContext.currentTime + note.start;
        const endsAt = startsAt + note.duration;

        oscillator.type = 'triangle';
        oscillator.frequency.setValueAtTime(note.frequency, startsAt);
        gain.gain.setValueAtTime(0.0001, startsAt);
        gain.gain.exponentialRampToValueAtTime(0.09, startsAt + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.0001, endsAt);

        oscillator.connect(gain);
        gain.connect(audioContext.destination);
        oscillator.start(startsAt);
        oscillator.stop(endsAt);
      });
    } catch (error) {
      console.error('Error playing browser notification sound', error);
    }
  }

  private isMobile(): boolean {
    return this._platform.ANDROID || this._platform.IOS;
  }
}
